import { usePocketBase } from '~/composables/pocketbase';

type NavigationType = 'page_load' | 'reload' | 'push' | 'replace' | 'traverse';

const EXCLUDED_PATH_PREFIXES = ['/admin'];
const SESSION_ID_KEY = 'poolimate_session_id';

let historyInstrumented = false;
let lastTrackedPage: string | null = null;

function isExcludedPage(path: string): boolean {
  return EXCLUDED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

function normalizePage(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

function createSessionId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `pv-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getSessionId(): string {
  try {
    let id = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = createSessionId();
      window.sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return createSessionId();
  }
}

function sendPageView(page: string, navigationType: NavigationType): void {
  try {
    if (isExcludedPage(page) || page === lastTrackedPage) {
      return;
    }
    lastTrackedPage = page;

    const pb = usePocketBase();
    const userId = pb.authStore.model?.id;

    const record = {
      page: normalizePage(page),
      url_full: window.location.href,
      navigation_type: navigationType,
      session_id: getSessionId(),
      user_agent_original: navigator.userAgent,
      ...(userId ? { user: userId } : {}),
    };

    const send = () => {
      pb.collection('page_views')
        .create(record)
        .catch(() => {});
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(send, { timeout: 2000 });
    } else {
      window.setTimeout(send, 0);
    }
  } catch {
    // tracking must never break the app
  }
}

function pathFromHistoryUrl(
  url: string | URL | null | undefined,
  baseUrl: string,
): string {
  if (!url) {
    return window.location.pathname;
  }
  try {
    return new URL(String(url), baseUrl).pathname;
  } catch {
    return window.location.pathname;
  }
}

function instrumentHistory(): void {
  if (historyInstrumented) {
    return;
  }
  historyInstrumented = true;

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function (
    this: History,
    ...args: Parameters<History['pushState']>
  ) {
    const baseUrl = window.location.href;
    const result = originalPushState.apply(this, args);
    sendPageView(pathFromHistoryUrl(args[2], baseUrl), 'push');
    return result;
  };

  window.history.replaceState = function (
    this: History,
    ...args: Parameters<History['replaceState']>
  ) {
    const baseUrl = window.location.href;
    const result = originalReplaceState.apply(this, args);
    sendPageView(pathFromHistoryUrl(args[2], baseUrl), 'replace');
    return result;
  };

  window.addEventListener('popstate', () => {
    sendPageView(window.location.pathname, 'traverse');
  });
}

export function initPageViewTracking(): void {
  try {
    if (process.server) {
      return;
    }

    instrumentHistory();

    const entries = performance.getEntriesByType('navigation');
    const entry = entries[entries.length - 1] as
      | PerformanceNavigationTiming
      | undefined;
    const navigationType: NavigationType =
      entry?.type === 'reload' ? 'reload' : 'page_load';
    sendPageView(window.location.pathname, navigationType);
  } catch {
    // ignore
  }
}
